import { useEffect } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ProgressBar, StatusBadge } from '@/shared/components';
import { useJobQuery, jobsService } from '@/features/jobs';
import { useJobWebSocket } from '@/features/jobs/hooks/useJobWebSocket';

interface TranslationProgressProps {
  jobId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export default function TranslationProgress({
  jobId,
  onComplete,
  onCancel,
}: TranslationProgressProps) {
  const { data: job } = useJobQuery(jobId);
  const wsState = useJobWebSocket(jobId, job);

  const cancelMutation = useMutation({
    mutationFn: () => jobsService.cancel(jobId),
  });

  // Trigger onComplete when job finishes
  useEffect(() => {
    if (
      wsState.status === 'completed' ||
      wsState.status === 'failed' ||
      wsState.status === 'cancelled'
    ) {
      // Small delay to show final state
      const timer = setTimeout(onComplete, 1000);
      return () => clearTimeout(timer);
    }
  }, [wsState.status, onComplete]);

  const handleCancel = async () => {
    if (window.confirm('¿Estás seguro de que deseas cancelar este proceso?')) {
      await cancelMutation.mutateAsync();
    }
  };

  const isActive = wsState.status === 'pending' || wsState.status === 'processing';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Estado del Proceso
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            ID: {jobId}
          </Typography>
        </Box>
        <StatusBadge status={wsState.status} size="medium" />
      </Box>

      <Box sx={{ mb: 3 }}>
        <ProgressBar
          value={wsState.progress}
          label={`${wsState.completed} de ${wsState.total} URLs procesadas`}
        />
      </Box>

      {wsState.isConnected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Conectado en tiempo real
        </Alert>
      )}

      {wsState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error: {wsState.error}
        </Alert>
      )}

      {isActive && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Cancelando...' : 'Cancelar Proceso'}
          </Button>
        </Box>
      )}

      {!isActive && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={onComplete}>
            Ver Resultados
          </Button>
          <Button variant="outlined" onClick={onCancel}>
            Nueva Traducción
          </Button>
        </Box>
      )}
    </Box>
  );
}
